import type { BookingConfirmation } from '@pa/api/public'
import { arriveByTime, Avatar, fmtLongDateOf, fmtTime, zonedParts } from '@pa/ui'
import { Check, Navigation, Phone, ShieldCheck } from 'lucide-react'
import { ScreenTitle } from '@/features/entry'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/**
 * P-12 "You're all set" (UserFlows P-12), the mockup's `TicketScreen`: built from `BookingConfirmation` plus the
 * reason held in memory ("Not provided" when empty; the server doesn't echo it, D-22). In-person visits get Arrive by
 * (start − 15 min) and a Directions link; telehealth shows the video copy. The Confirmation # is the PF appointment ID.
 */
export function TicketScreen({
  confirmation,
  reason,
  firstName,
  phone,
  timeZone,
}: {
  confirmation: BookingConfirmation
  reason: string | null
  firstName: string
  phone: string
  timeZone: string
}) {
  const { visitType, provider, location } = confirmation
  const tele = visitType.telehealth
  const local = zonedParts(confirmation.start, timeZone)
  const [y = 0, m = 1, d = 1] = local.date.split('-').map(Number)
  const weekday = WEEKDAYS_SHORT[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? ''
  return (
    <div style={{ maxWidth: 620, margin: '0 auto', width: '100%' }}>
      <div className="confirm-hero">
        <span className="success-icon" aria-hidden>
          <Check strokeWidth={3} />
        </span>
        <ScreenTitle>You’re all set, {firstName}!</ScreenTitle>
        <p className="muted">Your appointment is booked. See you soon.</p>
      </div>
      <div className="ticket">
        <div className="ticket-top">
          <div className="ticket-date" aria-hidden>
            <div className="m">{MONTHS_SHORT[m - 1]}</div>
            <div className="d">{d}</div>
            <div className="w">{weekday}</div>
          </div>
          <div>
            <h2>{visitType.name}</h2>
            <p>
              {fmtLongDateOf(confirmation.start, timeZone)} at {fmtTime(confirmation.start, timeZone)}
            </p>
          </div>
        </div>
        <div className="ticket-body">
          <div>
            <div className="t-label">Provider</div>
            <div className="row" style={{ gap: 10, marginTop: 6 }}>
              <Avatar name={provider.name} color={provider.color} size="sm" />
              <div>
                <b style={{ margin: 0 }}>{[provider.name, provider.credentials].filter((v) => v !== '').join(', ')}</b>
                {provider.specialty !== '' && <span>{provider.specialty}</span>}
              </div>
            </div>
          </div>
          <div>
            <div className="t-label">{tele ? 'Video visit' : 'Location'}</div>
            {/* The location's name, then its address only when it has one (task 07: a name shown once, no " · "). */}
            <b>{tele ? 'Link sent before your visit' : location.name}</b>
            {tele ? (
              <span>Join from your phone or computer</span>
            ) : (
              location.address !== '' && <span>{location.address}</span>
            )}
          </div>
          {!tele && confirmation.arriveBy !== null && (
            <div>
              <div className="t-label">Arrive by</div>
              <b>{arriveByTime(confirmation.start, timeZone)}</b>
              <span>15 minutes early for check-in</span>
            </div>
          )}
          <div>
            <div className="t-label">Reason for visit</div>
            <b style={{ fontWeight: reason ? 700 : 600, color: reason ? undefined : 'var(--muted)' }}>
              {reason ?? 'Not provided'}
            </b>
          </div>
        </div>
        <div className="ticket-perf" />
        <div className="ticket-foot">
          {!tele && location.address !== '' && (
            <a
              className="btn btn-secondary"
              href={`https://maps.google.com/?q=${encodeURIComponent(location.address)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Navigation aria-hidden /> Directions
            </a>
          )}
          <span className="spacer" style={{ flex: 1 }} />
          <span className="pf-ref" style={{ alignSelf: 'center' }}>
            <ShieldCheck aria-hidden /> Confirmation #{confirmation.confirmationNumber}
          </span>
        </div>
      </div>
      {phone !== '' && (
        <div className="contact-callout">
          <Phone aria-hidden />
          <p>
            To reschedule or cancel, please call the clinic at <a href={`tel:${phone.replace(/\D/g, '')}`}>{phone}</a>.
          </p>
        </div>
      )}
    </div>
  )
}
